import { FrameCapProps, FrameLayout, FrameMiddleProps } from './FrameLayout';
import { useBoundingBoxSize } from './frameUtils';
import { useK3ModelAndAction } from './useK3ModelAndAction';
import { K3Model, useActions } from '@k3/core';
import { BBAnchor } from '@react-three/drei';
import { memo, Suspense, useMemo, useRef } from 'react';
import { Group, Vector3, Vector3Tuple } from 'three';
import { Axis } from './AxisSelect';
import { K3ModelByKey } from './K3ModelsByKey';

const axisToIndex = (axis: Axis) => (axis === 'x' ? 0 : axis === 'y' ? 1 : 2);

const Zarge = ({
  modelKey,
  length,
  ctx,
}: {
  modelKey: string;
  length: number;
  ctx: any;
}) => {
  const { model, modelAction, component } = useK3ModelAndAction(modelKey);
  const { axis = 'x' } = ctx;

  const lengthKey = axis === 'x' ? 'width' : axis === 'y' ? 'height' : 'depth';
  const scale = useMemo(() => {
    const normalScale = [1, 1, 1] as Vector3Tuple;
    normalScale[axisToIndex(axis)] = length;
    return normalScale;
  }, [axis, length]);

  if (!model) {
    return null;
  }

  return component ? (
    <Suspense fallback={null}>
      <K3Model
        model={model}
        modelAction={modelAction}
        meshProps={{
          ...modelAction?.props,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          [lengthKey]: length,
        }}
        component={component}
        fileURL={model.path}
      />
    </Suspense>
  ) : (
    <Suspense fallback={null}>
      <group scale={scale}>
        <K3Model
          model={model}
          modelAction={modelAction}
          meshProps={modelAction?.props}
          component={component}
          fileURL={model.path}
        />
      </group>
    </Suspense>
  );
};

const MiddleFrame = memo(({ length, ctx }: FrameMiddleProps) => {
  const { middleTableKeys = [] } = ctx;
  const activeMiddleTableKeys = useActiveModelKeys(middleTableKeys);

  return (
    <BBAnchor anchor={[0, 1, 0]}>
      <group scale={[1, 1, 1]}>
        {activeMiddleTableKeys.map((modelKey) => (
          <Zarge key={modelKey} modelKey={modelKey} length={length} ctx={ctx} />
        ))}
      </group>
    </BBAnchor>
  );
});

const MirroredTableLeg = ({
  modelKey,
  ctx,
}: {
  modelKey: string;
  ctx: any;
}) => {
  const {
    model: tableLegModel,
    modelAction,
    component,
  } = useK3ModelAndAction(modelKey);

  const { axis = 'x' } = ctx;

  const scale = useMemo(() => {
    const scaleAxis = [2, 2, 0];
    const scale = [1, 1, 1];
    scale[scaleAxis[axisToIndex(axis)]] = -1;
    return scale as Vector3Tuple;
  }, [axis]);

  if (!tableLegModel || !modelAction) {
    return null;
  }

  return (
    <group>
      <Suspense fallback={null}>
        <K3Model
          model={tableLegModel}
          modelAction={modelAction}
          meshProps={modelAction.props}
          fileURL={tableLegModel.path}
          component={component}
        />
      </Suspense>

      <group scale={scale}>
        <Suspense fallback={null}>
          <K3Model
            model={tableLegModel}
            modelAction={modelAction}
            meshProps={modelAction.props}
            fileURL={tableLegModel.path}
            component={component}
          />
        </Suspense>
      </group>
    </group>
  );
};

const SingleTableLeg = memo(({ modelKey }: { modelKey: string }) => {
  return <K3ModelByKey modelKey={modelKey} />;
});

const TableLeg = memo(({ modelKey, ctx }: { modelKey: string; ctx: any }) => {
  const { mirroredTableLegKeys = [] } = ctx;

  const Component = useMemo(
    () =>
      mirroredTableLegKeys.includes(modelKey)
        ? MirroredTableLeg
        : SingleTableLeg,
    [mirroredTableLegKeys, modelKey]
  );

  return (
    <group key={modelKey}>
      <Component modelKey={modelKey} ctx={ctx} />
    </group>
  );
});

const TableConnector = ({ modelKey }: { modelKey: string }) => {
  return <K3ModelByKey modelKey={modelKey} />;
};

const emptyArray: string[] = [];
const defaultAxis: Axis = 'x';

const StartCap = memo(({ onSizeChange, ctx }: FrameCapProps) => {
  const ref = useRef<Group>();
  useBoundingBoxSize(ref.current, onSizeChange);

  const {
    tableLegKeys = emptyArray,
    mirroredTableLegKeys = emptyArray,
    connectorKeys = emptyArray,
    axis = defaultAxis,
  } = ctx;

  const allTableLegKeys = useMemo(
    () => [...tableLegKeys, ...mirroredTableLegKeys],
    [tableLegKeys, mirroredTableLegKeys]
  );

  const anchor = useMemo(() => {
    const anchor = [0, 1, 0];
    anchor[axisToIndex(axis)] = -1;
    return anchor as Vector3Tuple;
  }, [axis]);

  const activeTableLegKeys = useActiveModelKeys(allTableLegKeys);
  const activeConnectorKeys = useActiveModelKeys(connectorKeys);

  return (
    <BBAnchor anchor={anchor}>
      <group
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ref={ref}
      >
        {activeTableLegKeys.map((modelKey) => (
          <TableLeg key={modelKey} modelKey={modelKey} ctx={ctx} />
        ))}

        {activeConnectorKeys.map((modelKey) => (
          <TableConnector key={modelKey} modelKey={modelKey} />
        ))}
      </group>
    </BBAnchor>
  );
});

const EndCap = memo(({ onSizeChange, ctx }: FrameCapProps) => {
  const ref = useRef<Group>();
  useBoundingBoxSize(ref.current, onSizeChange);

  const {
    tableLegKeys = emptyArray,
    mirroredTableLegKeys = emptyArray,
    connectorKeys = emptyArray,
    axis = defaultAxis,
  } = ctx;

  const allTableLegKeys = useMemo(
    () => [...tableLegKeys, ...mirroredTableLegKeys],
    [tableLegKeys, mirroredTableLegKeys]
  );

  const axisIndex = axisToIndex(axis);
  const anchor = useMemo(() => {
    const anchor = [0, 1, 0];
    anchor[axisIndex] = 1;
    return anchor as Vector3Tuple;
  }, [axisIndex]);

  const scale = useMemo(() => {
    const scale = [1, 1, 1];
    scale[axisIndex] = -1;
    return scale as Vector3Tuple;
  }, [axisIndex]);

  const activeTableLegKeys = useActiveModelKeys(allTableLegKeys);
  const activeConnectorKeys = useActiveModelKeys(connectorKeys);

  return (
    <BBAnchor anchor={anchor}>
      <group
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ref={ref}
        scale={scale}
      >
        {activeTableLegKeys.map((modelKey) => (
          <TableLeg key={modelKey} modelKey={modelKey} ctx={ctx} />
        ))}

        {activeConnectorKeys.map((modelKey) => (
          <TableConnector key={modelKey} modelKey={modelKey} />
        ))}
      </group>
    </BBAnchor>
  );
});

const zeroVector = new Vector3(0, 0, 0);

export const NoCap = ({ onSizeChange }: FrameCapProps) => {
  onSizeChange(zeroVector);
  return null;
};

export type DynamicTableFrameExtraProps = {
  tableLegKeys: string[];
  mirroredTableLegKeys: string[];
  middleTableKeys: string[];
  connectorKeys: string[];
  middleLengthOffset: number;
  renderLeftCap: boolean;
  renderRightCap: boolean;
  axis: Axis;
};

export type FrameProps = {
  width: number;
  height: number;
  depth: number;
  startOffset: number;
  endOffset: number;
} & DynamicTableFrameExtraProps;

const emptyObj = {};

export const TableFrame = (props: FrameProps) => {
  return (
    <group>
      <Suspense fallback={null}>
        <FrameLayout
          debug={false}
          axis={props.axis}
          width={props.width}
          height={props.height}
          depth={props.depth}
          startOffset={props.startOffset}
          endOffset={props.endOffset}
          MiddleCap={MiddleFrame}
          StartCap={props.renderRightCap ? StartCap : NoCap}
          EndCap={props.renderLeftCap ? EndCap : NoCap}
          ctx={props ?? emptyObj}
        />
      </Suspense>
    </group>
  );
};

function useActiveModelKeys(allTableLegKeys: string[]) {
  const actions = useActions();
  const modelActions = actions?.models ?? [];

  const activeKeys = useMemo(
    () =>
      allTableLegKeys.reduce((acc, key) => {
        const modelAction = modelActions.find((ma) => ma.model.key === key);
        if (modelAction) {
          acc.push(key);
        }
        return acc;
      }, [] as string[]),
    [modelActions]
  );
  return activeKeys;
}
